import type {
  BackofficeReportChangeSignal,
  BackofficeReportReadFamily,
} from './reportChangeSignal';
import { clearReportDetailReadFamilyCache } from './reportDetailReadFamily';
import type { BackofficeReportProjection, ReportMapPoint } from './reportsGateway';

export type ReportReadInvalidationTagType =
  | 'ReportsList'
  | 'ReportsDetail'
  | 'ReportsMap'
  | 'ReportsHeatmap';

export interface ReportReadInvalidationTag {
  type: ReportReadInvalidationTagType;
}

export type ReportPresenceLossReason = 'reportPresenceLost';

type ReportListPresenceFilter = {
  field: string;
  op: string;
  value: string | string[];
};

type ReportPresenceLossRecord = {
  reason: ReportPresenceLossReason;
};

const REPORT_READ_FAMILY_INVALIDATION_TAGS: Record<
  BackofficeReportReadFamily,
  ReportReadInvalidationTag[]
> = {
  list: [{ type: 'ReportsList' }],
  detail: [{ type: 'ReportsDetail' }],
  map: [{ type: 'ReportsMap' }],
  heatmap: [{ type: 'ReportsHeatmap' }],
};

const REPORT_READ_INVALIDATION_TAG_ORDER: ReportReadInvalidationTagType[] = [
  'ReportsList',
  'ReportsDetail',
  'ReportsMap',
  'ReportsHeatmap',
];

const reportPresenceLosses = new Map<string, ReportPresenceLossRecord>();

const dedupeReportReadInvalidationTags = (
  tags: ReportReadInvalidationTag[]
): ReportReadInvalidationTag[] => {
  const seen = new Set<ReportReadInvalidationTagType>();
  return tags.filter((tag) => {
    if (seen.has(tag.type)) {
      return false;
    }

    seen.add(tag.type);
    return true;
  });
};

export const sortReportReadInvalidationTags = (
  tags: ReportReadInvalidationTag[]
): ReportReadInvalidationTag[] =>
  [...tags].sort(
    (left, right) =>
      REPORT_READ_INVALIDATION_TAG_ORDER.indexOf(left.type) -
      REPORT_READ_INVALIDATION_TAG_ORDER.indexOf(right.type)
  );

export const buildReportReadFamilyInvalidationTags = (
  signal: BackofficeReportChangeSignal
): ReportReadInvalidationTag[] =>
  sortReportReadInvalidationTags(
    dedupeReportReadInvalidationTags(
      signal.affectedReadFamilies.flatMap((family) => REPORT_READ_FAMILY_INVALIDATION_TAGS[family])
    )
  );

export const recordReportPresenceLost = (
  reportId: string,
  reason: ReportPresenceLossReason = 'reportPresenceLost'
): void => {
  reportPresenceLosses.set(reportId, { reason });
  clearReportDetailReadFamilyCache(reportId);
};

export const resetReportPresenceLossPolicy = (): void => {
  reportPresenceLosses.clear();
};

export const isReportPresenceLost = (reportId: string): boolean =>
  reportPresenceLosses.has(reportId);

export const getReportPresenceLossReason = (
  reportId: string
): ReportPresenceLossReason | undefined => reportPresenceLosses.get(reportId)?.reason;

export const getLastValidReportListPage = (totalCount: number, pageSize: number): number =>
  totalCount > 0 ? Math.max(0, Math.ceil(totalCount / pageSize) - 1) : 0;

const getReportListFilterValue = (
  report: BackofficeReportProjection,
  field: string
): string | undefined => {
  switch (field) {
    case 'reviewStatus':
    case 'species':
    case 'habitatType':
    case 'observationType':
    case 'sightingDate':
      return report[field];
    default:
      return undefined;
  }
};

const matchesReportListFilter = (
  report: BackofficeReportProjection,
  filter: ReportListPresenceFilter
): boolean => {
  const reportValue = getReportListFilterValue(report, filter.field);
  if (reportValue === undefined) {
    return false;
  }

  switch (filter.op) {
    case 'equals':
    case 'onDate':
      return typeof filter.value === 'string' && reportValue === filter.value;
    case 'notEquals':
      return typeof filter.value === 'string' && reportValue !== filter.value;
    case 'isAnyOf':
      return Array.isArray(filter.value) && filter.value.includes(reportValue);
    case 'before':
      return typeof filter.value === 'string' && reportValue < filter.value;
    case 'after':
      return typeof filter.value === 'string' && reportValue > filter.value;
    case 'onOrBefore':
      return typeof filter.value === 'string' && reportValue <= filter.value;
    case 'onOrAfter':
      return typeof filter.value === 'string' && reportValue >= filter.value;
    default:
      return false;
  }
};

export const doesReportMatchListFilters = (
  report: BackofficeReportProjection,
  filters: ReportListPresenceFilter[]
): boolean => filters.every((filter) => matchesReportListFilter(report, filter));

export const reconcileReportPresenceLostListPage = <
  T extends { items: BackofficeReportProjection[]; totalCount: number },
>(
  page: T,
  reportId: string,
  targetTotalCount?: number
): T => {
  const items = page.items.filter((report) => report.id !== reportId);
  const didRemoveItem = items.length < page.items.length;
  const fallbackTotalCount = didRemoveItem ? Math.max(0, page.totalCount - 1) : page.totalCount;
  const totalCount =
    targetTotalCount === undefined
      ? fallbackTotalCount
      : Math.min(page.totalCount, Math.max(0, targetTotalCount));

  return {
    ...page,
    items,
    totalCount,
  };
};

export const pruneReportPresenceLostListPage = <
  T extends { items: BackofficeReportProjection[]; totalCount: number },
>(
  page: T
): T => {
  const items = page.items.filter((report) => !isReportPresenceLost(report.id));
  const removedCount = page.items.length - items.length;

  return {
    ...page,
    items,
    totalCount: Math.max(0, page.totalCount - removedCount),
  };
};

export const pruneReportPresenceLostMapPoints = <T extends ReportMapPoint[]>(points: T): T =>
  points.filter((point) => !isReportPresenceLost(point.id)) as T;

export const clearReportReviewDeletionConsequences = (reportId: string): void => {
  recordReportPresenceLost(reportId);
};
